<script setup>
import { __, getMonthString } from '@/utils';
import debounce from 'lodash/debounce';
import {
    Combobox,
    ComboboxInput,
    ComboboxOptions,
    ComboboxOption,
	ComboboxButton,
} from '@headlessui/vue';
import { ref, onMounted } from 'vue'

const emit = defineEmits(['filtered']);

const props = defineProps({
    items: {
        type: Array,
        required: true
    }, 
    showSearch: Boolean,
    btnClass: String,
    isInvoice: Boolean
});

const searchValue = ref('');
const years = ref([]);
const months = ref([
  { name: getMonthString(0), numeric: 0 },
  { name: getMonthString(1), numeric: 1 },
  { name: getMonthString(2), numeric: 2 },
  { name: getMonthString(3), numeric: 3 },
  { name: getMonthString(4), numeric: 4 },
  { name: getMonthString(5), numeric: 5 },
  { name: getMonthString(6), numeric: 6 },
  { name: getMonthString(7), numeric: 7 },
  { name: getMonthString(8), numeric: 8 },
  { name: getMonthString(9), numeric: 9 },
  { name: getMonthString(10), numeric: 10 },
  { name: getMonthString(11), numeric: 11 }
]);
const selectedStartYear = ref('');
const selectedEndtYear = ref('');
const selectedStartMonth = ref('');
const selectedEndtMonth = ref('');
const showMobileFrom = ref(false);
const showMobileTo = ref(false);

const filterDocuments = () => {
    const startDate = new Date(selectedStartYear.value, selectedStartMonth.value.numeric, 1);
    const endDate = new Date(
        selectedEndtYear.value,
        selectedEndtMonth.value.numeric + 1, 
        0, // last day of the month
        23, // hours
        59, // minutes
        59, // seconds
        999 // milliseconds
    );

    const data = {
        startDate: startDate,
        endDate: endDate,
        search: searchValue.value
    }

    emit('filtered', data)
}

const handleChange = debounce((event) => {
    searchValue.value = event.target.value.trim();

    filterDocuments();

}, 300);

onMounted(() => {
    const startYear = 2024;
    const currentYear = new Date().getFullYear();
    const yearsArray = [];

    for (let year = startYear; year <= currentYear; year++) {
        yearsArray.push(year);
    }
    years.value = yearsArray;
});
</script>
<template>
    <div class="scroll-filters__text" :class="{'is-clicked': showMobileFrom}">
        <h5>{{ __('From the date') }}</h5>
        <button type="button" class="scroll-filters__toggle" @click="showMobileFrom = !showMobileFrom">
            {{ __('Show') }}
            <span>
                <i class="ico ico--chevron-down"></i>
            </span>
        </button>
    </div>
    <div class="combobox-outer combobox-outer--fullbutton" :class="{ 'combobox-outer--open': showMobileFrom }">
        <Combobox v-model="selectedStartMonth">
            <div class="combobox-wrapper">
                <ComboboxInput
                    :displayValue="(value) => value.name" 
                    id="startMonth"
                    name="startMonth"
                    disabled
                />
                <ComboboxButton class="combobox-icon" />
            </div>
            <ComboboxOptions class="combobox-options notice__options">
                <ComboboxOption
                    v-for="option in months"
                    :key="option"
                    :value="option"
                    class="combobox-option combobox-option--smaller"
                >
                    {{ option.name }}
                </ComboboxOption>
            </ComboboxOptions>
        </Combobox>
    </div>
    <div class="combobox-outer combobox-outer--fullbutton" :class="{ 'combobox-outer--open': showMobileFrom }">
        <Combobox v-model="selectedStartYear">
            <div class="combobox-wrapper">
                <ComboboxInput
                    :displayValue="(value) => value" 
                    id="startYears"
                    name="startYears"
                    disabled
                />
                <ComboboxButton class="combobox-icon" />
            </div>
            <ComboboxOptions class="combobox-options notice__options">
                <ComboboxOption
                    v-for="option in years"
                    :key="option"
                    :value="option"
                    class="combobox-option combobox-option--smaller"
                >
                    {{ option }}
                </ComboboxOption>
            </ComboboxOptions>
        </Combobox>
    </div>

    <div class="scroll-filters__text"  :class="{'is-clicked': showMobileTo}" >
        <h5>{{ __('Until the date') }}</h5>
        <button type="button" class="scroll-filters__toggle" @click="showMobileTo = !showMobileTo">
            {{ __('Show') }}
            <span>
                <i class="ico ico--chevron-down"></i>
            </span>
        </button>
    </div>
    <div class="combobox-outer combobox-outer--fullbutton" :class="{ 'combobox-outer--open': showMobileTo }">
        <Combobox v-model="selectedEndtMonth">
            <div class="combobox-wrapper">
                <ComboboxInput
                    :displayValue="(value) => value.name"
                    id="endMonth"
                    name="endMonth"
                    disabled
                />
                <ComboboxButton class="combobox-icon" />
            </div>
            <ComboboxOptions class="combobox-options notice__options">
                <ComboboxOption
                    v-for="option in months"
                    :key="option"
                    :value="option"
                    class="combobox-option combobox-option--smaller"
                >
                    {{ option.name }}
                </ComboboxOption>
            </ComboboxOptions>
        </Combobox>
    </div>
    <div class="combobox-outer combobox-outer--fullbutton" :class="{ 'combobox-outer--open': showMobileTo }">
        <Combobox v-model="selectedEndtYear">
            <div class="combobox-wrapper">
                <ComboboxInput
                    :displayValue="(value) => value" 
                    id="endYears"
                    name="endYears"
                    disabled
                />
                <ComboboxButton class="combobox-icon" />
            </div>
            <ComboboxOptions class="combobox-options notice__options">
                <ComboboxOption
                    v-for="option in years"
                    :key="option"
                    :value="option"
                    class="combobox-option combobox-option--smaller"
                >
                    {{ option }}
                </ComboboxOption>
            </ComboboxOptions>
        </Combobox>
    </div>

    <div class="scroll-filters-btn" :class="{'is-visible': showMobileFrom || showMobileTo}">
        <button :class="btnClass" @click.prevent="filterDocuments()">
            {{ __('Displaying results') }}
        </button>
    </div>

    <div class="search-package__head" v-if="showSearch">
        <div class="search-package__head-input">
            <Combobox v-model="searchValue">
                <div class="combobox-wrapper">
                    <ComboboxInput
                        :displayValue="(value) => value"
                        @change="handleChange"
                        id="searchValue"
                        name="searchValue"
                        placeholder="או חפש כאן..."
                    />
                    <label for="searchValue" class="profile--search-icon">
                        <i>
                            <img :src="`${theme_path.url}/resources/images/svg/search.svg`" alt="Search Location Icon" />
                        </i>
                    </label>
                </div>
            </Combobox>
        </div>
    </div><!-- /.search-package__head -->
</template>